import type { LegacyRef } from 'react'

import type { GetStaticProps, NextPage } from 'next'
import dynamic from 'next/dynamic'

import classNames from 'classnames'

import type { BreadcrumbProps } from '~/components/atoms/Breadcrumb/Breadcrumb'
import SlideOutPanel from '~/components/atoms/SlideOutPanel/SlideOutPanel'
import DirectoryHeader from '~/components/directory/DirectoryHeader/DirectoryHeader'
import FeaturedPartners from '~/components/directory/FeaturedPartners/FeaturedPartners'
import HeaderCTA from '~/components/directory/HeaderCTA/HeaderCTA'
import LargePartnerResultsCard from '~/components/directory/LargePartnerResultsCard/LargePartnerResultsCard'
import LocationFilter from '~/components/directory/LocationFilter/LocationFilter'
import type { PartnerCardProps } from '~/components/directory/PartnerCard/PartnerCard'
import PartnerResults from '~/components/directory/PartnerResults/PartnerResults'
import { ReportTheme } from '~/components/report/types'
import { resolveBlock } from '~/components/serializers'
import { useDirectory } from '~/hooks/useDirectory'
import {
  createBaseSelector,
  detectBodyModuleTypes,
  getBodyModules,
  internationalizedField,
  link,
  myPortfolioBanner,
  noDrafts,
  partnerCard,
} from '~/schemas/common'
import { getFAQData } from '~/schemas/JsonLd'
import { useSanity } from '~/services/sanity'
import { buildBreadcrumb, getBreadcrumbJsonLd } from '~/utils/breadcrumb'
import { addTranslations, loadMessages, type Locale } from '~/utils/i18n'
import { createLangStaticPaths } from '~/utils/langPaths'
import { metaQuery } from '~/utils/seo'
import { resolvePageSeoMeta } from '~/utils/seoHelper'
import { resolveUrlLinksWithTranslation, urlQuery } from '~/utils/urlResolver'
import { withLocalizedSettings } from '~/utils/withLocalizedSettings'

import styles from '~/styles/pages.module.css'

const MapWidget = dynamic(
  () => import('~/components/directory/MapWidget/MapWidget')
)

type Categories = {
  name: string
  slug: string
  categoryOrder: number
}

interface PageData {
  body: any
  partners: PartnerCardProps[] // using for testing
  categories: Categories[]
  sponsoredPartner?: any
}

const Directory: NextPage<PageData> = ({
  partners,
  categories,
  sponsoredPartner,
  body,
}: PageData) => {
  const {
    categoryClickHandler,
    nearMeClickHandler,
    selectedFilter,
    dynamicPartners,
    nearMeActive,
    canLoadMore,
    loadMoreHandler,
    chunkedPartners,
    resultsRef,
    listViewHandler,
    mapViewHandler,
    listRef,
    showLocationDrawer,
    locationClickHandler,
    locationChangeHandler,
    selectedLocation,
    cancelLocationClickHandler,
  } = useDirectory(partners)

  return (
    <>
      <HeaderCTA variant="light" />
      <DirectoryHeader
        title={'The<em>Directory</em>'}
        summary={body?.summary}
        categories={categories.sort(
          (a, b) => a.categoryOrder - b.categoryOrder
        )}
        locationClickHandler={() => {
          locationClickHandler()
        }}
        cancelLocationClickHandler={cancelLocationClickHandler}
        categoryClickHandler={categoryClickHandler}
        nearMeClickHandler={nearMeClickHandler}
        selectedFilter={selectedFilter}
        nearMeActive={nearMeActive}
        selectedLocation={selectedLocation}
      />
      <FeaturedPartners
        theme="dark"
        useDivider
        partners={partners && partners.filter(partner => partner.isFeatured)}
        hideTitle
      />
      <div ref={resultsRef as LegacyRef<HTMLDivElement>}>
        {dynamicPartners?.length > 0 && (
          <MapWidget
            partners={dynamicPartners}
            listViewHandler={listViewHandler}
            mapViewHandler={mapViewHandler}
          />
        )}
      </div>
      {sponsoredPartner && (
        <LargePartnerResultsCard partner={sponsoredPartner} flip />
      )}
      <div ref={listRef as LegacyRef<HTMLDivElement>}>
        <PartnerResults
          partners={chunkedPartners}
          canLoadMore={canLoadMore}
          loadMoreHandler={loadMoreHandler}
        />
      </div>

      {body?.body && (
        <div className={classNames(styles.body, styles.directoryBody)}>
          {body.body.map((block: any, i: any) => resolveBlock(block, i))}
        </div>
      )}

      <SlideOutPanel
        isShowing={showLocationDrawer}
        closeHandler={() => locationClickHandler(false)}
        title="Location"
        closeColour="black"
        theme={ReportTheme.secondary}
      >
        <LocationFilter
          selectedLocation={selectedLocation}
          locationChangeHandler={(location, isPostcode, range) => {
            locationChangeHandler(location, isPostcode, range)
          }}
        />
      </SlideOutPanel>
    </>
  )
}

export const getStaticPropsOriginal: GetStaticProps =
  async function getStaticPropsOriginal(context) {
    try {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const { client, scorer, limiter } = await useSanity()
      const lang = (context?.params?.lang as Locale) || 'en'
      const messages = await loadMessages(lang)

      // STEP 1: Pre-query to detect which body module types are actually used
      const baseSelector = createBaseSelector(
        'page',
        [`slug.current == $slug`],
        limiter,
        scorer
      )
      const detectedTypes = await detectBodyModuleTypes(
        client,
        baseSelector,
        { slug: 'directory' },
        ['body']
      )

      // STEP 2: Main query with only the required body modules
      const query = `{
        "body": *[_type == 'page' && slug.current == $slug ${limiter}] ${scorer} [0] {
          ${urlQuery},
          ${metaQuery('page')},
          "summary": summary[]${resolveUrlLinksWithTranslation(lang)}{
            ...
          },
          redirectUrl,
          title,
          hasGoogleReviews,
          fullGoogleReviews,
          breadCrumbHidden,
          "body": body[]${resolveUrlLinksWithTranslation(lang)} {
            ${getBodyModules(lang, detectedTypes)}
          },
          ${link},
          relatedArticlesTitle,
        },
        ${link},
        ${myPortfolioBanner}
        "meta": *[_type == 'page' && slug.current == $slug ${limiter}] ${scorer}[0]{ meta, _id },
        "partners": *[_type == 'partner'] ${partnerCard},
        "categories": *[_type == 'category' && type == 'partner' ${noDrafts}] {
          "name": ${internationalizedField('name')},
          "slug": slug.current,
          categoryOrder
        },
        "sponsoredPartner": *[_type == 'page' && slug.current == 'directory'] [0] {
          "sponsoredPartner": sponsoredPartner->{
            ...,
            "category": category[]->{
              "name": ${internationalizedField('name')},
              "slug": slug.current,
              categoryOrder
            }
          }
        }
      }`

      const { meta: metaProps, ...data } = await client.fetch(query, {
        slug: 'directory',
        lang,
      })

      const breadcrumb: BreadcrumbProps['list'] | undefined = buildBreadcrumb(
        ['the-directory'],
        '',
        undefined,
        messages,
        lang
      )
      const breadcrumbJsonLd = breadcrumb ? getBreadcrumbJsonLd(breadcrumb) : []

      // Use centralized SEO helper with locale-aware templates
      await resolvePageSeoMeta(context, metaProps, 'page')

      return {
        props: {
          notFound: false,
          smallHeader: true,
          meta: metaProps.meta,
          url: '/directory',
          ...data,
          sponsoredPartner: data.sponsoredPartner?.sponsoredPartner,
          breadcrumb,
          breadcrumbJsonLd,
          hasGoogleReviews: data.body.hasGoogleReviews,
          noAnimatedTheme: true,
          darkFooter: true,
          theme: 'dark',
          jsonLd: {
            faqJsonLd: getFAQData(data.body?.body),
          },
        },
      }
    } catch (e) {
      console.log(e)
      return {
        notFound: true,
      }
    }
  }

// Add SSR translation support
export const getStaticProps = withLocalizedSettings(async context => {
  const result = await getStaticPropsOriginal(context)
  if ('notFound' in result || 'redirect' in result) {
    return result // Return notFound or redirect directly
  }
  return { props: await addTranslations(result.props, context) }
})
export const getStaticPaths = async () => createLangStaticPaths()

export default Directory
